import React, { useEffect, useState } from 'react'
import { isNil, xor } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { formatSummaryTableContent, formatTime } from '@/utils/helpers'

import { AiAnswersAnswerDrawer } from './AiAnswersAnswerDrawer'

const NO_GAPS_HEADER = 'No knowledge gaps'
const NO_GAPS_TEXT = 'No unanswered questions in this date range.'
const UNAVAILABLE_HEADER = 'Not available yet'
const UNAVAILABLE_TEXT = "This version of AgentGPT doesn't report unanswered questions."

/**
 * @import import { AiAnswersKnowledgeGaps } from '@/views/AiAnswers/AiAnswersKnowledgeGaps'
 * @param {object} props
 * @param {object[]} props.rows The page's search log rows, passed as the store array itself; only
 *   those with `no_answer` are listed, and a new array (a refetch) clears the selection
 * @param {boolean} props.loading Shows the table loader while the search log fetches; a fetch
 *   starting clears the selection
 * @param {number} props.organizationId The org the answer is written to
 * @param {object} [props.verifiedAnswers] The org's verified answers; null while loading or after a failed load,
 *   and a refetch clears the selection
 * @param {boolean} props.verifiedAnswersLoading Holds the table loader, so answered asks never flash in
 * @param {object} props.playbooks The org's playbook options and their load state, for scoping an answer
 * @param {boolean} props.canAnswer Offers selection and the answer action
 * @param {Function} props.onAnswered Called after an answer saves
 */
export const AiAnswersKnowledgeGaps = ({
  rows,
  loading,
  organizationId,
  verifiedAnswers,
  verifiedAnswersLoading,
  playbooks,
  canAnswer,
  onAnswered,
}) => {
  const [selectedIds, setSelectedIds] = useState([])
  const answeredIds = isNil(verifiedAnswers)
    ? []
    : verifiedAnswers.items.flatMap((item) => item.source_agent_question_ids)
  // An agent-gpt that predates `no_answer` omits it, and an empty table would then
  // read as "no gaps"; an empty range still reports zero, since `[].every()` is true.
  const gapsAvailable = rows.every((row) => !isNil(row.no_answer))
  const gaps = gapsAvailable
    ? rows.filter((row) => row.no_answer && !answeredIds.includes(row.id))
    : []

  // A new org or date range starts a fetch; clearing on loading too covers a failed
  // fetch, which leaves the previous rows in place under the new filter. Verified
  // answers refetch after any write, which can answer away a selected gap.
  useEffect(() => {
    setSelectedIds([])
  }, [rows, loading, verifiedAnswers])

  const columns = [
    {
      accessor: 'created_at',
      label: 'Created On',
      collapsing: true,
      format: (value) => formatTime(value),
    },
    {
      accessor: 'agent_name',
      label: 'Agent',
      isSearchable: true,
      collapsing: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Agents',
      },
    },
    {
      accessor: 'question',
      label: 'Question',
      isSearchable: true,
      isCopiable: true,
      format: (value) => <div>{formatSummaryTableContent(value)}</div>,
    },
  ]

  const handleBulkSelectSingle = (rowId) => {
    setSelectedIds((currentIds) => xor(currentIds, [rowId]))
  }

  const handleSelectAll = (rowsToSelect) => {
    setSelectedIds(rowsToSelect.map((row) => row.id))
  }

  const handleDeselectAll = () => {
    setSelectedIds([])
  }

  const handleAnswered = () => {
    setSelectedIds([])
    onAnswered()
  }

  return (
    <Card className="no-padding" data-testid="ai-answers-knowledge-gaps">
      <AdvancedTable
        loading={loading || verifiedAnswersLoading}
        rows={gaps}
        columns={columns}
        pagination
        defaultOrderBy="created_at"
        defaultOrder="desc"
        itemName="question"
        noDataHeader={gapsAvailable ? NO_GAPS_HEADER : UNAVAILABLE_HEADER}
        noDataText={gapsAvailable ? NO_GAPS_TEXT : UNAVAILABLE_TEXT}
        bulkSelectable={canAnswer}
        selectedRows={selectedIds}
        handleBulkSelectSingle={handleBulkSelectSingle}
        handleSelectAll={handleSelectAll}
        handleDeselectAll={handleDeselectAll}
        bulkSelectAction={
          <AiAnswersAnswerDrawer
            asks={gaps.filter((row) => selectedIds.includes(row.id))}
            verifiedAnswers={verifiedAnswers}
            playbooks={playbooks}
            organizationId={organizationId}
            onAnswered={handleAnswered}
          />
        }
      />
    </Card>
  )
}
